import AlgoliaKeysManager from './algolia-keys-manager';
import { fetchAlgoliaKeys } from '../../api/algolia-api-keys';

// Singleton service so the whole app shares one cache, one refresh timer,
// and one in-flight refresh promise.
const manager = new AlgoliaKeysManager({
  fetcher: async () => {
    // `fetchAlgoliaKeys` returns the backend payload; the manager normalizes expiry fields.
    return await fetchAlgoliaKeys();
  },
  // Refresh before expiry to reduce the chance of in-flight requests failing.
  refreshBufferMs: 60 * 1000,
});

/**
 * Get Algolia keys without forcing a refresh if keys exist and have sufficient buffer time remaining.
 * This is useful for avoiding unnecessary API calls when keys are still valid.
 * 
 * Note: We check for buffer time to prevent using keys that might expire during request execution.
 * This matches the manager's "fresh enough" logic to ensure consistency.
 */
export const getAlgoliaKeysIfAvailable = () => {
  // Access manager's internal keys directly to avoid forcing refresh
  const keys = manager._keys;
  if (!keys) return null;
  
  const now = Date.now();
  const expiresAt = keys.expiresAt;
  const refreshBufferMs = manager.refreshBufferMs;
  
  // Return keys only if they exist, are not expired, AND have sufficient buffer time remaining
  // This prevents using keys that might expire during request execution
  if (expiresAt && expiresAt > now && expiresAt - now > refreshBufferMs) {
    return keys;
  }
  // Keys expired or don't have enough buffer time, return null
  return null;
};

/**
 * Get Algolia keys, waiting for them to be available if needed.
 * The manager handles retries with exponential backoff internally.
 * If keys are still unavailable after manager's retries, throw immediately.
 */
export const getAlgoliaKeys = async () => {
  const keys = await manager.getKeys();
  if (!keys) {
    // Manager has already exhausted its retries with exponential backoff.
    // Don't add artificial delays - throw immediately to avoid UX degradation.
    throw new Error('Algolia keys not available. API server may not be configured.');
  }
  return keys;
};

export const ensureFreshAlgoliaKeys = async opts => {
  return await manager.ensureFresh(opts);
};


